import React, { useContext, useState, useRef } from 'react';

import { ALERT_TYPE, CUENTA_STATE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, TRIBUTO_TYPE } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { ServerRequest } from '../../utils/apiweb';
import { useFilterPersistence, useNavigateWithFilters } from '../../components/hooks';
import { createSearchWithSavedFilters } from '../../utils/searchWithSavedFilters';
import {
    Loading,
    TableCustom,
    MessageModal,
    SectionHeading,
    WrapperAdvancedSearch
} from '../../components/common';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import CrearCuentaModal from '../../components/controls/CrearCuentaModal';
import { ManagedContext } from '../../context/custom';
import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { useLista } from '../../components/hooks/useLista';
import { usePermissions } from '../../components/hooks/usePermissions';
import { PERMISSIONS } from '../../consts/permissions';
import {TIPS} from "../../consts/tips";
import { parseSerializedIds } from '../../utils/convert';
import { buildVehiculoEntityInit, isVehiculoGateValid } from '../VehiculoView/utils';
import VehiculoGateFields from '../VehiculoView/components/VehiculoGateFields';

function VehiculosView() {

    //hooks
    const { showMessageModal } = useContext(ManagedContext);

    const [tableVisible, setTableVisible] = useState(false);
    const [searchMessage, setSearchMessage] = useState(null);
    const [createModal, setCreateModal] = useState({ show: false, idIncisoVehiculo: 0, vehiculoMenor: false, idTipoVehiculo: 0, anioModelo: '', dominio: '' });
    const [state, setState] = useState({
        loading: false,
        showMessage: false,
        rowId: 0,
        dataTagger: {
            showModal: false,
            idEntidad: null
        },
        list: []
    });

    const searchRef = useRef();
    const tableRef = useRef();

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.VEHICULO_EDIT);
    const canReactivar = hasPermission(PERMISSIONS.CUENTA_EDIT);

    const tabsConfig = [
        {
            key: 'cuenta',
            title: 'Cuenta',
            fields: ['numeroCuenta', 'idPersona', 'idEstadoCuenta', 'etiqueta', 'idsTipoCondicionEspecial', 'idsTipoRecargoDescuento'],
            buildUrl: (filters) =>
                `/cuenta/filter?` +
                `numeroCuenta=${encodeURIComponent(filters.numeroCuenta)}&` +
                `idEstadoCuenta=${filters.idEstadoCuenta}&` +
                `numeroWeb=&numeroDocumento=&` +
                `idPersona=${filters.idPersona}&` +
                `etiqueta=${encodeURIComponent(filters.etiqueta)}&` +
                `idsTipoCondicionEspecial=${encodeURIComponent(parseSerializedIds(filters.idsTipoCondicionEspecial))}&` +
                `idsTipoRecargoDescuento=${encodeURIComponent(parseSerializedIds(filters.idsTipoRecargoDescuento))}`
        },
        {
            key: 'datosVehiculo',
            title: 'Datos del vehículo',
            fields: ['dominio', 'marcaModelo'],
            buildUrl: (filters) =>
                `/datos/filter?` +
                `dominio=${encodeURIComponent(filters.dominio)}&` +
                `marcaModelo=${encodeURIComponent(filters.marcaModelo)}`
        }
    ];

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
            .then((error) => {
                const message = error.message;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => ({ ...prevState, loading: false }));
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => ({ ...prevState, loading: false }));
            });
    };

    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => ({ ...prevState, loading: false }));
    };

    const SearchVehiculosWithSavedFilters = createSearchWithSavedFilters({
        setState,
        setTableVisible,
        apiUrl: APIS.URLS.VEHICULO,
        tabsConfig,
        callbackNoSuccess,
        callbackError,
        setSearchMessage
    });

    const { getCurrentFilters, restoredPageIndex } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchVehiculosWithSavedFilters
    );
    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters);

    //definiciones
    const cellA = (props) => !canEdit ? '' : (
        <div className='action'>
            <div onClick={() => handleClickVehiculoAdd()} className="link">
                <span className="material-symbols-outlined" title="Nuevo">add</span>
            </div>
        </div>
    );

    const cellVMR = (props) => (
        <div className='action'>
            <div onClick={() => handleClickVehiculoView(props.value)} className="link">
                <span className="material-symbols-outlined" title="Ver">search</span>
            </div>
            {canEdit && props.row.original.idEstadoCuenta !== CUENTA_STATE.INACTIVA && (
                <>
                    <div onClick={() => handleClickVehiculoModify(props.value)} className="link">
                        <span className="material-symbols-outlined" title="Modificar">stylus</span>
                    </div>
                    <div onClick={() => handleClickVehiculoRemove(props.value)} className="link">
                        <span className="material-symbols-outlined" title="Eliminar">delete</span>
                    </div>
                </>
            )}
            {canReactivar && props.row.original.idEstadoCuenta === CUENTA_STATE.INACTIVA && (
                <div onClick={() => handleClickReactivar(props.row.original.idCuenta, props.row.original.numeroCuenta)} className="link">
                    <span className="material-symbols-outlined" title="Reactivar">unarchive</span>
                </div>
            )}
            <div onClick={() => handleClickVehiculoDataTagger(props.value)} className="link">
                <span className="material-symbols-outlined" title="Información adicional">info</span>
            </div>
        </div>
    );

    const [, getRowLista] = useLista({
        listas: ['EstadoCuenta'],
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: {
            key: 'EstadoCuenta',
            timeout: 0
        }
    });

    const getDescEstadoCuenta = (id) => {
        const row = getRowLista('EstadoCuenta', id);
        return (row) ? row.nombre : '';
    };

    const tableColumns = [
        { Header: 'N° cuenta', accessor: 'numeroCuenta', width: '25%' },
        { Header: 'Dominio', accessor: 'dominio', width: '20%' },
        {
            Header: 'Marca-Modelo',
            Cell: (props) => {
                const row = props.row.original;
                const marcaModelo = `${row.marca} ${row.modelo}`;
                return marcaModelo;
            },
            accessor: 'marcaModelo',
            width: '35%'
        },
        {
            Header: 'Estado cuenta',
            Cell: (props) => getDescEstadoCuenta(props.value),
            accessor: 'idEstadoCuenta',
            width: '20%'
        },
        {
            Header: cellA,
            Cell: cellVMR,
            id: 'abm',
            accessor: 'id',
            width: '10%',
            disableGlobalFilter: true,
            disableSortBy: true
        }
    ];

    //handles
    const handleClickVehiculoAdd = () => {
        setCreateModal({ show: true, idIncisoVehiculo: 0, vehiculoMenor: false, idTipoVehiculo: 0, anioModelo: '', dominio: '' });
    };
    function CreateVehiculo() {
        setState(prevState => ({ ...prevState, loading: true }));
        setCreateModal(prevState => ({ ...prevState, show: false }));

        const base = buildVehiculoEntityInit();
        const dataBody = {
            ...base,
            vehiculo: {
                ...base.vehiculo,
                idIncisoVehiculo: createModal.idIncisoVehiculo,
                idTipoVehiculo: createModal.idTipoVehiculo,
                anioModelo: createModal.anioModelo,
                dominio: createModal.dominio
            },
            // el número de cuenta de Vehículo se deriva del dominio
            cuenta: {
                ...base.cuenta,
                numeroCuenta: createModal.dominio
            }
        };

        const callbackSuccess = (response) => {
            response.json()
                .then((row) => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    navigateWithFilters('/vehiculo/' + OPERATION_MODE.EDIT + '/' + row.vehiculo.id);
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };

        ServerRequest(
            REQUEST_METHOD.POST,
            null,
            true,
            APIS.URLS.VEHICULO,
            null,
            dataBody,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }
    const createModalValid = isVehiculoGateValid(createModal);
    const handleClickVehiculoView = (id) => {
        navigateWithFilters('/vehiculo/' + OPERATION_MODE.VIEW + '/' + id);
    };
    const handleClickVehiculoModify = (id) => {
        navigateWithFilters('/vehiculo/' + OPERATION_MODE.EDIT + '/' + id);
    };
    const handleClickVehiculoRemove = (id) => {
        const vehiculo = state.list.find(x => x.id === id);
        showMessageModal({
            ...MESSAGE_MODALS.BORRAR,
            message: `¿Está seguro de borrar el número de cuenta ${vehiculo.dominio}?`,
            onConfirm: () => RemoveVehiculo(id),
        });
    };
    const handleClickReactivar = (idCuenta, numeroCuenta) => {
        showMessageModal({
            title: "Confirmación",
            message: `¿Está seguro de reactivar el número de cuenta ${numeroCuenta}?`,
            onConfirm: () => ReactivarCuenta(idCuenta),
        });
    };
    const handleClickVehiculoDataTagger = (id) => {
        setState(prevState => ({ ...prevState, dataTagger: { showModal: true, idEntidad: id } }));
    };

    //funciones
    function SearchVehiculos() {
        return SearchVehiculosWithSavedFilters(getCurrentFilters());
    }

    function RemoveVehiculo(id) {
        setState(prevState => ({ ...prevState, loading: true }));

        const callbackSuccess = (response) => {
            response.json()
                .then(() => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    SearchVehiculos();
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.VEHICULO,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    function ReactivarCuenta(idCuenta) {
        setState(prevState => ({ ...prevState, loading: true }));

        const callbackSuccess = (response) => {
            response.json()
                .then(() => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'La cuenta fue reactivada correctamente');
                    SearchVehiculos();
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };

        const paramsUrl = `/reactivar/${idCuenta}`;

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.CUENTA,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    const dataTaggerRow = state.list.find(x => x.id === state.dataTagger.idEntidad)
    const dataTaggerRegistro = dataTaggerRow ? `el dominio ${dataTaggerRow.dominio}` : ''

    return (
        <>
            <Loading visible={state.loading} />

            {state.dataTagger.showModal && (
                <DataTaggerModalApi
                    title="Información adicional de vehiculo"
                    registro={dataTaggerRegistro}
                    tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_VEHICULO_MODAL}
                    entidad="Vehiculo"
                    idEntidad={state.dataTagger.idEntidad}
                    disabled={!canEdit}
                    onConfirm={() => {
                        setState(prevState => ({ ...prevState, dataTagger: { showModal: false, idEntidad: null } }));
                    }}
                    onDismiss={() => {
                        setState(prevState => ({ ...prevState, dataTagger: { showModal: false, idEntidad: null } }));
                    }}
                />
            )}

            {state.showMessage && (
                <MessageModal
                    title={"Confirmación"}
                    message={"¿Está seguro de borrar el registro?"}
                    onDismiss={() => {
                        setState(prevState => ({ ...prevState, showMessage: false, rowId: 0 }));
                    }}
                    onConfirm={() => {
                        const id = state.rowId;
                        setState(prevState => ({ ...prevState, showMessage: false, rowId: 0 }));
                        RemoveVehiculo(id);
                    }}
                />
            )}

            {createModal.show && (
                <CrearCuentaModal
                    canConfirm={createModalValid}
                    onConfirm={CreateVehiculo}
                    onDismiss={() => setCreateModal({ show: false, idIncisoVehiculo: 0, vehiculoMenor: false, idTipoVehiculo: 0, anioModelo: '', dominio: '' })}
                >
                    <VehiculoGateFields
                        gate={createModal}
                        onChange={(patch) => setCreateModal(prevState => ({ ...prevState, ...patch }))}
                    />
                </CrearCuentaModal>
            )}

            <SectionHeading
                titles={[{ title: 'Tributos' }, { title: 'Administración de vehículos' }]}
                onAddClick={canEdit ? handleClickVehiculoAdd : null}
                tableVisible={tableVisible}
            />

            <section className='section-accordion'>
                <WrapperAdvancedSearch
                    ref={searchRef}
                    message={searchMessage}
                    onClear={() => {
                        setState(prevState => ({ ...prevState, list: [] }));
                        setTableVisible(false);
                    }}
                    tabs={tabsConfig}
                    filters={[
                        { title: 'Número de cuenta', field: 'numeroCuenta', type: 'string', size: 3, searchOnEnter: true },
                        { title: 'Persona', field: 'idPersona', type: 'persona', size: 5 },
                        { title: 'Estado', field: 'idEstadoCuenta', type: 'selectLista', lista: 'EstadoCuenta', presetValue: 2, presetLabel: 'Activa', size: 2 },
                        { title: 'Etiqueta', field: 'etiqueta', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Condición especial', field: 'idsTipoCondicionEspecial', type: 'entityMultiple', entidad: 'TipoCondicionEspecial', filter: row => !row.idTipoTributo || row.idTipoTributo === TRIBUTO_TYPE.VEHICULOS, size: 6 },
                        { title: 'Recargo descuento', field: 'idsTipoRecargoDescuento', type: 'entityMultiple', entidad: 'TipoRecargoDescuento', size: 6 },
                        { title: 'Dominio', field: 'dominio', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Marca-Modelo', field: 'marcaModelo', type: 'string', size: 5, searchOnEnter: true },
                    ]}
                    onSearch={SearchVehiculos}
                />

                {tableVisible && (
                    <TableCustom
                        ref={tableRef}
                        className={'TableCustomBase m-top-20'}
                        columns={tableColumns}
                        data={state.list}
                        restoredPageIndex={restoredPageIndex}
                        messageEmpty="No se encontraron resultados"
                    />
                )}
            </section>
        </>
    );
}

export default VehiculosView;

